import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextButton,
  Button,
  Box,
  DropdownBase,
  Input,
  Divider,
} from '@wix/design-system';
import CommentItem from './CommentItem';
import s from './CommentsList.scss';
import {
  COMMENT_STATUS,
  EDITOR_TYPE,
  SITE_EDITOR_LINK,
  COMMENT_STATUS_VALUES,
} from '../../utils/constants';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Duplicate, Dismiss, Edit, Check } from '@wix/wix-ui-icons-common';
import logger from '../../utils/biLogger';
import {
  getFeedbackClickedCopyLink,
  getFeedbackClickOnOptionDropdownMenu,
  getFeedbackClickOnDropdownMenu,
  getFeedbackViewComment,
} from '@wix/bi-logger-get-feedback/v2';
import _ from 'lodash';
import { useAppContext, ACTIONS } from '../App/AppContext';

const OPTIONS_IDS = {
  OPEN_THREADS: 0,
  RESOLVED_THREADS: 1,
  ALL_THREADS: 2,
};

const getCommentStatusValue = _comment =>
  COMMENT_STATUS_VALUES[_comment.status];
const getCommentLastUpdatedDateValue = _comment => {
  const commentUpdatedDate = new Date(_comment.updatedDate).getTime();
  if (!_.isEmpty(_comment.replies)) {
    const replyLastCreatedDate = new Date(
      _comment.replies[_comment.replies.length - 1].createdDate,
    ).getTime();
    return _.max([replyLastCreatedDate, commentUpdatedDate]);
  }
  return commentUpdatedDate;
};

const sortComments = _comments =>
  _.orderBy(
    _comments,
    [getCommentStatusValue, getCommentLastUpdatedDateValue],
    ['desc', 'desc'],
  );

const CommentsList = ({
  isShown,
  onOpenComment,
  onSetFilterOpenComments,
  onSetFilterResolvedComments,
  resolvedFilterOn,
  openFilterOn,
  comments = [],
}) => {
  const { t } = useTranslation();
  const {
    state: { shareUrl, editorType, role, metaSiteId },
    dispatch,
  } = useAppContext();
  const [filteredComments, setFilteredComments] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    OPTIONS_IDS.OPEN_THREADS,
  );
  const [open, setOpen] = useState(false);
  const options = [
    {
      id: OPTIONS_IDS.OPEN_THREADS,
      value: t('get_feedback_comments_list_show_open_threads'),
      label: t('get_feedback_comments_list_show_open_threads'),
    },
    {
      id: OPTIONS_IDS.RESOLVED_THREADS,
      value: t('get_feedback_comments_list_show_resolved_threads'),
      label: t('get_feedback_comments_list_show_resolved_threads'),
    },
    {
      id: OPTIONS_IDS.ALL_THREADS,
      value: t('get_feedback_comments_list_show_all_thread'),
      label: t('get_feedback_comments_list_show_all_thread'),
    },
  ];
  useEffect(() => {
    const sortedComments = sortComments(comments);
    if (resolvedFilterOn && openFilterOn) {
      setFilteredComments(sortedComments);
      return;
    }

    if (openFilterOn) {
      setFilteredComments(
        sortedComments.filter(
          ({ status }) =>
            status === COMMENT_STATUS.OPEN || status === COMMENT_STATUS.UNREAD,
        ),
      );
      return;
    }

    if (resolvedFilterOn) {
      setFilteredComments(
        sortedComments.filter(
          ({ status }) => status === COMMENT_STATUS.RESOLVED,
        ),
      );
      return;
    }

    setFilteredComments([]);
  }, [comments, openFilterOn, resolvedFilterOn]);

  function handleCopyClicked() {
    logger.report(
      getFeedbackClickedCopyLink({
        origin: 'comments',
        editorVersion: editorType,
        user_type: role,
      }),
    );

    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 1000);
  }

  function handleFilterOnSelect(option) {
    setSelectedOption(option.id);
    setOpen(false);
    if (option.id === OPTIONS_IDS.OPEN_THREADS) {
      logger.report(
        getFeedbackClickOnOptionDropdownMenu({
          origin: 'view-comment-page',
          editorVersion: editorType,
          user_type: role,
          option: 'open_comments',
        }),
      );
      onSetFilterOpenComments(true);
      onSetFilterResolvedComments(false);
      return;
    }

    if (option.id === OPTIONS_IDS.RESOLVED_THREADS) {
      logger.report(
        getFeedbackClickOnOptionDropdownMenu({
          origin: 'view-comment-page',
          editorVersion: editorType,
          user_type: role,
          option: 'resolved_comments',
        }),
      );

      onSetFilterResolvedComments(true);
      onSetFilterOpenComments(false);
      return;
    }

    logger.report(
      getFeedbackClickOnOptionDropdownMenu({
        origin: 'view-comment-page',
        editorVersion: editorType,
        user_type: role,
        option: 'all_comments',
      }),
    );

    onSetFilterResolvedComments(true);
    onSetFilterOpenComments(true);
  }

  function renderFilters() {
    return (
      <div className={s.listHeader}>
        <Box align="right" marginBottom={2} marginTop={1}>
          <TextButton
            size="tiny"
            suffixIcon={<Dismiss size="12" />}
            onClick={() =>
              dispatch({
                type: ACTIONS.SET_SHOULD_SHOW_COMMENTS_LIST,
                payload: false,
              })
            }
            style={{ fontWeight: 'bold' }}
            dataHook="close-comment-list-btn"
          >
            {t('get_feedback_share_lb_close_cta')}
          </TextButton>
        </Box>
        <DropdownBase
          style={{ width: '100%' }}
          dataHook="drop-down-base"
          open={open}
          onClickOutside={() => setOpen(false)}
          options={options}
          selectedId={selectedOption}
          onSelect={option => handleFilterOnSelect(option)}
        >
          <Input
            menuArrow
            dataHook="drop-down-opener"
            value={options[selectedOption].label}
            disableEditing
            onInputClicked={() => {
              logger.report(
                getFeedbackClickOnDropdownMenu({
                  origin: 'view-comment-page',
                  editorVersion: editorType,
                  user_type: role,
                }),
              );
              setOpen(true);
            }}
          />
        </DropdownBase>
      </div>
    );
  }

  function renderCommentsListFooterCTAButtons() {
    return (
      <>
        <Divider />
        <Box height="60px" bottom="0" width="348px" display="flex">
          <Box verticalAlign="middle" paddingLeft="24px">
            <CopyToClipboard text={shareUrl}>
              <TextButton
                prefixIcon={
                  copySuccess ? <Check size="24" /> : <Duplicate size="24" />
                }
                size="small"
                onClick={handleCopyClicked}
                dataHook="copy-link"
              >
                {copySuccess
                  ? t('get_feedback_comments_list_copy_shareable_link_copied')
                  : t('get_feedback_comments_list_copy_shareable_link')}
              </TextButton>
            </CopyToClipboard>
          </Box>
          <Box verticalAlign="middle" marginLeft="auto" paddingRight="24px">
            <Button
              priority="secondary"
              prefixIcon={<Edit size="24" />}
              size="small"
              onClick={() =>
                window.open(`${SITE_EDITOR_LINK}${metaSiteId}`, '_blank')
              }
              dataHook="edit-link"
            >
              {t('get_feedback_comments_list_edit_site')}
            </Button>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <div
      className={`${s.root} ${isShown ? s.shown : ''} ${
        editorType === EDITOR_TYPE.STUDIO ? s.studioRoot : s.classicRoot
      }`}
      data-hook="comments-list-wrapper"
    >
      {renderFilters()}
      <Box position="relative" direction="vertical">
        <div
          className={`${s.commentsWrapper} ${
            editorType === EDITOR_TYPE.STUDIO ? s.studioHeight : s.classicHeight
          }`}
        >
          {filteredComments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onOpenComment={_comment => {
                logger.report(
                  getFeedbackViewComment({
                    origin: 'list-view',
                    editorVersion: editorType,
                    comment_id: _comment.id,
                    commentStatus: _comment.status,
                    breakPoint: JSON.stringify(
                      _comment.commentLocation.breakpoint,
                    ),
                  }),
                );
                onOpenComment(_comment);
              }}
            />
          ))}
        </div>
        <Box
          position="absolute"
          bottom="0"
          right="8px"
          background="linear-gradient(360deg, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)"
          height="32px"
          width="95%"
        ></Box>
      </Box>
      {renderCommentsListFooterCTAButtons()}
    </div>
  );
};

export default CommentsList;
